import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, IconButton, CircularProgress, Paper, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  createLeadSchedule, updateLeadSchedule, getJoinedGreenUsers, setStoreData
} from '../../../Redux/actions';
import {
  inputLabelFocusedStyle, selectFocusedStyle, buttonStyles, datePickerSlotProps
} from '../../../theme/formStyles';

const themedTextFieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

const meetingTypeOptions = ['In Person', 'Online'];
const statusOptions = ['Open', 'Closed'];

const emptyScheduleEntry = () => ({ schedule_date: dayjs().format('YYYY-MM-DD'), meeting_type: 'In Person', description: '', status: 'Open', closing_comment: '', locked: false });

export default function LeadScheduleForm({ editingSchedule, leads, onBack, onSaved }) {
  const dispatch = useDispatch();
  const isEdit = !!editingSchedule;
  const [loading, setLoading] = useState(false);
  const [greenUsers, setGreenUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [entryErrors, setEntryErrors] = useState([]);

  const [selectedClient, setSelectedClient] = useState(isEdit ? editingSchedule.lead?.client_name : '');
  const [selectedLeadId, setSelectedLeadId] = useState(isEdit ? editingSchedule.lead?.id : '');

  const [form, setForm] = useState({
    location: isEdit ? (editingSchedule.location || '') : '',
    bd_owner: isEdit ? editingSchedule.bd_owner : null,
  });

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const [entries, setEntries] = useState(
    isEdit && editingSchedule.entries?.length > 0
      ? editingSchedule.entries.map(e => ({
          schedule_date: e.schedule_date,
          meeting_type: e.meeting_type || 'In Person',
          description: e.description || '',
          status: e.status || 'Open',
          closing_comment: e.closing_comment || '',
          locked: e.status === 'Closed',
        }))
      : [emptyScheduleEntry()]
  );

  // Frozen at first render — only the entry's *persisted* status counts, so a user can't just
  // flip the latest entry to Closed in this session, add a new one, then flip it back to Open.
  const [canAddEntry] = useState(() => {
    if (!isEdit) return false;
    const initialEntries = editingSchedule.entries || [];
    return initialEntries[initialEntries.length - 1]?.status === 'Closed';
  });

  // Only one new schedule entry can be added per form session — once added, disable
  // the button again until it's saved (re-opening the form resets this).
  const [initialEntryCount] = useState(() => (isEdit ? (editingSchedule.entries?.length || 0) : entries.length));
  const canAddMore = canAddEntry && entries.length <= initialEntryCount;

  useEffect(() => {
    dispatch(getJoinedGreenUsers()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        let users = res.data.allData || res.data.data || [];
        users = users.filter((u) => (u.user_id || '').toLowerCase().includes('@greenvironment'));
        setGreenUsers(users);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientOptions = Array.from(new Set(leads.map(l => l.client_name)));
  const opportunityOptions = leads.filter(l => l.client_name === selectedClient);

  const handleClientChange = (value) => {
    setSelectedClient(value);
    setSelectedLeadId('');
    if (errors.client) setErrors(prev => ({ ...prev, client: null }));
  };

  const handleOpportunityChange = (leadId) => {
    setSelectedLeadId(leadId);
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setForm(prev => ({ ...prev, location: lead.location || prev.location }));
    }
    if (errors.opportunity) setErrors(prev => ({ ...prev, opportunity: null }));
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const addEntry = () => {
    if (!canAddMore) return;
    setEntries([...entries, emptyScheduleEntry()]);
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
    if (field === 'closing_comment' && entryErrors[index]) {
      const updatedEntryErrors = [...entryErrors];
      updatedEntryErrors[index] = null;
      setEntryErrors(updatedEntryErrors);
    }
  };

  const validate = () => {
    const err = {};
    if (!isEdit && !selectedClient) err.client = 'Client name is required';
    if (!isEdit && !selectedLeadId) err.opportunity = 'Opportunity is required';
    if (!form.bd_owner) err.bd_owner = 'BD Owner / SPOC is required';
    if (entries.length === 0 || entries.some(e => !e.schedule_date)) {
      err.entries = 'Each schedule entry needs a date';
    }

    const newEntryErrors = entries.map((e) =>
      e.status === 'Closed' && !e.closing_comment ? 'Closing comment is required' : null
    );
    setEntryErrors(newEntryErrors);

    setErrors(err);
    return Object.keys(err).length === 0 && !newEntryErrors.some(Boolean);
  };

  const handleSubmitClick = () => {
    if (!validate()) return;

    const latestStatus = entries[entries.length - 1]?.status;
    if (latestStatus === 'Closed') {
      setShowCloseConfirm(true);
      return;
    }

    performSubmit();
  };

  const performSubmit = () => {
    setShowCloseConfirm(false);

    const payload = {
      ...form,
      entries: entries.map(({ locked, ...entry }) => entry),
      lead_id: isEdit ? editingSchedule.lead_id : selectedLeadId,
    };

    setLoading(true);
    const action = isEdit ? updateLeadSchedule(editingSchedule.id, payload) : createLeadSchedule(payload);

    dispatch(action).then((res) => {
      setLoading(false);
      if ((res?.status === 200 || res?.status === 201) && res.data?.success) {
        dispatch(setStoreData('alertMessage', {
          msg: res.data.message || (isEdit ? 'Schedule updated successfully' : 'Schedule created successfully'),
          type: 'success',
          handleClose: () => {
            dispatch(setStoreData('alertMessage', null));
            onSaved && onSaved();
          },
        }));
      } else {
        dispatch(setStoreData('alertMessage', {
          msg: res?.data?.message || 'Something went wrong',
          type: 'fail',
          handleClose: () => dispatch(setStoreData('alertMessage', null)),
        }));
      }
    });
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h6">
          {isEdit ? 'Edit BD Client Meet Schedule' : 'BD Client Meet Schedule'}
        </Typography>
      </Box>

      <Paper sx={{ padding: 3 }}>
        <Grid container spacing={2}>

          <Grid item xs={12} md={6}>
            {isEdit ? (
              <TextField fullWidth label="Client Name" value={selectedClient || ''} disabled sx={themedTextFieldSx} />
            ) : (
              <FormControl fullWidth error={!!errors.client}>
                <InputLabel sx={inputLabelFocusedStyle}>Client Name *</InputLabel>
                <Select
                  value={selectedClient}
                  label="Client Name *"
                  onChange={(e) => handleClientChange(e.target.value)}
                  sx={selectFocusedStyle}
                  MenuProps={{ disableScrollLock: true }}
                >
                  {clientOptions.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
                {errors.client && <Typography color="error" variant="caption">{errors.client}</Typography>}
              </FormControl>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {isEdit ? (
              <TextField fullWidth label="Opportunity" value={editingSchedule.lead?.opportunity_name || ''} disabled sx={themedTextFieldSx} />
            ) : (
              <FormControl fullWidth error={!!errors.opportunity} disabled={!selectedClient}>
                <InputLabel sx={inputLabelFocusedStyle}>Opportunity *</InputLabel>
                <Select
                  value={selectedLeadId}
                  label="Opportunity *"
                  onChange={(e) => handleOpportunityChange(e.target.value)}
                  sx={selectFocusedStyle}
                  MenuProps={{ disableScrollLock: true }}
                >
                  {opportunityOptions.map(lead => (
                    <MenuItem key={lead.id} value={lead.id}>{lead.opportunity_name}</MenuItem>
                  ))}
                </Select>
                {errors.opportunity && <Typography color="error" variant="caption">{errors.opportunity}</Typography>}
              </FormControl>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location (type)"
              placeholder="City / area"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={greenUsers}
              getOptionLabel={(option) => option.name ? (option.user_id ? `${option.name} (${option.user_id})` : option.name) : ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={form.bd_owner}
              onChange={(e, newValue) => handleChange('bd_owner', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="BD Owner / SPOC *"
                  error={!!errors.bd_owner}
                  helperText={errors.bd_owner}
                  sx={themedTextFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>Schedules *</Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              The latest row is what shows on the calendar. Once a row is marked Closed it's locked — add a new row to keep tracking.
            </Typography>
            {errors.entries && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                {errors.entries}
              </Typography>
            )}
          </Grid>

          {entries.map((entry, index) => (
            <React.Fragment key={index}>
              <Grid item xs={12} md={2}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <DatePicker
                    label="Date"
                    format="DD/MM/YYYY"
                    value={entry.schedule_date ? dayjs(entry.schedule_date) : null}
                    onChange={(newVal) => updateEntry(index, 'schedule_date', newVal ? newVal.format('YYYY-MM-DD') : '')}
                    slotProps={datePickerSlotProps({ fullWidth: true })}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel sx={inputLabelFocusedStyle}>Meeting Type</InputLabel>
                  <Select
                    value={entry.meeting_type}
                    label="Meeting Type"
                    onChange={(e) => updateEntry(index, 'meeting_type', e.target.value)}
                    sx={selectFocusedStyle}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {meetingTypeOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth disabled={entry.locked}>
                  <InputLabel sx={inputLabelFocusedStyle}>Status</InputLabel>
                  <Select
                    value={entry.status}
                    label="Status"
                    onChange={(e) => updateEntry(index, 'status', e.target.value)}
                    sx={selectFocusedStyle}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={entry.status === 'Closed' ? 3 : 6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={entry.description}
                  onChange={(e) => updateEntry(index, 'description', e.target.value)}
                  sx={themedTextFieldSx}
                />
              </Grid>
              {entry.status === 'Closed' && (
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Closing comment *"
                    value={entry.closing_comment}
                    onChange={(e) => updateEntry(index, 'closing_comment', e.target.value)}
                    error={!!entryErrors[index]}
                    helperText={entryErrors[index]}
                    sx={themedTextFieldSx}
                  />
                </Grid>
              )}
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button
              startIcon={<AddIcon />}
              onClick={addEntry}
              variant="outlined"
              size="small"
              disabled={!canAddMore}
              sx={buttonStyles.outlined}
            >
              Add Schedule
            </Button>
            {!canAddMore && (
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                {!canAddEntry
                  ? 'Mark the latest entry as Closed before adding a new one.'
                  : 'A new schedule has already been added — save it before adding another.'}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, bgcolor: '#f1faf4', borderRadius: 2 }}>
              <CalendarMonthIcon sx={{ color: '#196a3e', mt: 0.3 }} fontSize="small" />
              <Typography variant="body2" sx={{ color: '#196a3e' }}>
                The latest schedule above will appear on the Master Schedule under the selected SPOC, as long as status is Open — alongside Calibration, Project Tasks, and Ticket Site Visits.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" onClick={onBack} sx={buttonStyles.outlined}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitClick}
                disabled={loading}
                sx={buttonStyles.contained}
              >
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (isEdit ? 'Update schedule' : 'Save & add to calendar')}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Paper>

      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#222' }}>Close this schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Once closed, this entry is locked and can't be reopened — you'll need to add a new schedule entry to keep tracking. It will also stop appearing on the Master Schedule calendar.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowCloseConfirm(false)} variant="outlined" sx={buttonStyles.outlined}>
            Cancel
          </Button>
          <Button onClick={performSubmit} variant="contained" sx={buttonStyles.contained}>
            Confirm & Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
